import Link from "next/link";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Clock, ExternalLink, EyeOffIcon } from "lucide-react";

import { ForgeHistoryLog } from "@coinbot/forge-api/trpc";

import { Button, Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui";
import { useTxHistoryDetailsByHistoryLog } from "./useTxHistoryDetailsByHistoryLog";

export const TransactionHistoryRow = ({
  historyLog,
  onHide,
}: {
  historyLog: ForgeHistoryLog;
  onHide?: (historyLog: ForgeHistoryLog) => void;
}) => {
  const { title, explorerUrl, icon } = useTxHistoryDetailsByHistoryLog(historyLog);

  return (
    <motion.div
      layout
      transition={{ duration: 0.15 }}
      key={`transactions-history-row-${historyLog?.txHash}`}
      className="flex items-start justify-between space-x-2 border-b border-white/10 py-2"
    >
      <div className="mt-0.5">{icon}</div>

      <div className="w-full text-sm font-medium leading-6 text-[#D6D3CC] *:inline-flex *:align-top">
        {title}
      </div>

      <div className="ml-auto mt-0.5 flex gap-x-1">
        {historyLog?.createdAt && (
          <Tooltip>
            <TooltipTrigger>
              <Button variant="icon" className="cursor-default rounded-xs">
                <Clock className="h-4 w-4" />
              </Button>
            </TooltipTrigger>

            <TooltipContent>
              {format(historyLog?.createdAt, "cccc, yyyy-MM-dd HH:mm:ss")}
            </TooltipContent>
          </Tooltip>
        )}

        {explorerUrl && (
          <Tooltip>
            <TooltipTrigger>
              <Link target="_blank" rel="noreferrer noopener" href={explorerUrl}>
                <Button variant="icon" className="rounded-xs">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </Link>
            </TooltipTrigger>

            <TooltipContent>Check transaction details in Explorer</TooltipContent>
          </Tooltip>
        )}

        <Tooltip>
          <TooltipTrigger>
            <Button variant="icon" className="rounded-xs" onClick={() => onHide?.(historyLog)}>
              <EyeOffIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>

          <TooltipContent>Hide transaction</TooltipContent>
        </Tooltip>
      </div>
    </motion.div>
  );
};
